import React, { useState } from 'react';
import { Search, X, Truck } from 'lucide-react';

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://fleet.host/api'
  : '/api';

interface SearchFilters {
  licensePlate: string;
  nickname: string;
  vin: string;
  driver: string;
}

interface Vehicle {
  vehicle_id: number;
  license_plate: string;
  nickname?: string;
  vin?: string;
  make_name?: string;
  model_name?: string;
  year?: number;
  vehicle_type?: string;
  current_driver?: {
    driver_name: string;
  };
  last_location?: {
    location?: {
      point?: {
        lat: number;
        lng: number;
      };
      address?: {
        location?: string;
      };
    };
  };
}

interface Props {
  onClose: () => void;
  onSelectVehicle?: (vehicle: Vehicle) => void;
}

const VehicleSearch: React.FC<Props> = ({ onClose, onSelectVehicle }) => {
  const [filters, setFilters] = useState<SearchFilters>({
    licensePlate: '',
    nickname: '',
    vin: '',
    driver: ''
  });
  const [results, setResults] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    try {
      setLoading(true);
      setError(null);
      setSearched(true);

      const wildcardFilter: any = {};
      if (filters.licensePlate) wildcardFilter.license_plate = `*${filters.licensePlate}*`;
      if (filters.nickname) wildcardFilter.nickname = `*${filters.nickname}*`;
      if (filters.vin) wildcardFilter.vin = `*${filters.vin}*`;

      const body = {
        wildcard_filter: wildcardFilter,
        driver_filter: filters.driver || '',
        realtime_fields: ['LastLocation', 'Driver']
      };

      const response = await fetch(`${API_BASE_URL}/ituran-vehicle-search.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (result.success) {
        const vehicles = result.data.items || result.data || [];
        setResults(vehicles);
      } else {
        setError(result.error || 'Search failed');
      }
    } catch (err) {
      setError('Failed to search vehicles');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFilters({
      licensePlate: '',
      nickname: '',
      vin: '',
      driver: ''
    });
    setResults([]);
    setSearched(false);
    setError(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Search className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-bold">Advanced Vehicle Search</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search Filters */}
        <div className="p-4 border-b bg-gray-50">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                License Plate
              </label>
              <input
                type="text"
                value={filters.licensePlate}
                onChange={(e) => setFilters({ ...filters, licensePlate: e.target.value })}
                onKeyPress={handleKeyPress}
                placeholder="e.g., KH47"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fleet Number
              </label>
              <input
                type="text"
                value={filters.nickname}
                onChange={(e) => setFilters({ ...filters, nickname: e.target.value })}
                onKeyPress={handleKeyPress}
                placeholder="e.g., Truck"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                VIN
              </label>
              <input
                type="text"
                value={filters.vin}
                onChange={(e) => setFilters({ ...filters, vin: e.target.value })}
                onKeyPress={handleKeyPress}
                placeholder="e.g., 1HGBH"
                className="w-full px-3 py-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Driver Name
              </label>
              <input
                type="text"
                value={filters.driver}
                onChange={(e) => setFilters({ ...filters, driver: e.target.value })}
                onKeyPress={handleKeyPress}
                placeholder="e.g., John"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={handleSearch}
              disabled={loading || (!filters.licensePlate && !filters.nickname && !filters.vin && !filters.driver)}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Search className="w-4 h-4" />
              {loading ? 'Searching...' : 'Search'}
            </button>
            <button
              onClick={handleClear}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-auto p-4">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {!loading && searched && results.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Search className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No vehicles found</p>
              <p className="text-sm mt-2">Try adjusting your search criteria</p>
            </div>
          )}

          {!loading && results.length > 0 && (
            <div>
              <div className="mb-4 text-sm text-gray-600">
                Found {results.length} vehicle{results.length !== 1 ? 's' : ''}
              </div>
              <div className="space-y-2">
                {results.map((vehicle) => (
                  <div
                    key={vehicle.vehicle_id}
                    className="border rounded-lg p-4 hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer"
                    onClick={() => onSelectVehicle && onSelectVehicle(vehicle)}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Truck className="w-6 h-6 text-blue-600" />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-bold text-lg text-gray-900">
                            {vehicle.license_plate}
                          </span>
                          {vehicle.nickname && (
                            <span className="text-gray-600">({vehicle.nickname})</span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                          {vehicle.make_name && vehicle.model_name && (
                            <div className="text-gray-600">
                              <span className="font-medium">Vehicle:</span> {vehicle.make_name} {vehicle.model_name} {vehicle.year}
                            </div>
                          )}
                          {vehicle.vehicle_type && (
                            <div className="text-gray-600">
                              <span className="font-medium">Type:</span> {vehicle.vehicle_type}
                            </div>
                          )}
                          {vehicle.vin && (
                            <div className="text-gray-600">
                              <span className="font-medium">VIN:</span> {vehicle.vin}
                            </div>
                          )}
                          {vehicle.current_driver && (
                            <div className="text-gray-600">
                              <span className="font-medium">Driver:</span> {vehicle.current_driver.driver_name}
                            </div>
                          )}
                          {vehicle.last_location?.location?.address?.location && (
                            <div className="text-gray-600 col-span-2">
                              <span className="font-medium">Location:</span> {vehicle.last_location.location.address.location}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleSearch;
